import { ActionIcon, Box, Card, Group, Menu, Stack } from '@mantine/core';
import { Text } from '@repo/foundations';
import { IconDots, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import type { IReport } from '../../api';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useDeleteReport } from '../AnalyticsListPage/AnalyticsListPage.hooks';
import { isDefaultReport as isReadOnlyReport } from '../AnalyticsPage/utils/utils';
import { useStyles } from './AnalyticsListPage.styles';
import { DeleteReportModal } from './DeleteReportModal';
import ReportImageGrid from './ReportImageGrid';
interface AnalyticsReportCardProps {
	report: IReport;
	onClick?: () => void;
}

const getDaysAgo = (dateString: string) => {
	const updatedDate = new Date(dateString);
	const now = new Date();
	const diffTime = Math.abs(now.getTime() - updatedDate.getTime());
	const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

	return diffDays === 0 ? 'Today' : `${diffDays} days ago`;
};

export default function AnalyticsReportCard({
	report,
	onClick,
}: AnalyticsReportCardProps) {
	const { classes, theme } = useStyles();
	const { isOpen, openModal, closeModal, handleDelete } = useDeleteReport();
	const [isHovered, setIsHovered] = useState(false);
	const readOnly = isReadOnlyReport(report);
	const { aiImpactDashboard } = useFeatureFlags();
	const reportName =
		aiImpactDashboard && report.type === 'd_ai' ? 'AI Analytics' : report.name;

	const handleCardClick = (e: React.MouseEvent) => {
		if (e.defaultPrevented) {
			return;
		}
		onClick?.();
	};

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		openModal(report.id);
	};

	return (
		<>
			<DeleteReportModal
				isOpen={isOpen}
				onCancel={closeModal}
				onConfirm={handleDelete}
			/>
			<Card
				className={classes.card}
				onClick={handleCardClick}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<Box className={classes.imageContainer}>
					<ReportImageGrid widgets={report.widgets} reportType={report.type} />
					{isHovered && !readOnly && (
						<Menu
							position="bottom-end"
							withArrow
							withinPortal
							styles={{
								dropdown: {
									zIndex: 1000,
									maxWidth: '150px',
									minWidth: '150px',
								},
							}}
						>
							<Menu.Target>
								<ActionIcon
									className={classes.actionIcon}
									h={28}
									w={28}
									variant="transparent"
									onClick={(e) => e.stopPropagation()}
								>
									<IconDots
										size={theme.spacing.md}
										color={theme.other.getColor('icon/primary/default')}
									/>
								</ActionIcon>
							</Menu.Target>
							<Menu.Dropdown>
								<Menu.Item
									icon={<IconTrash size={14} />}
									onClick={handleDeleteClick}
								>
									Delete
								</Menu.Item>
							</Menu.Dropdown>
						</Menu>
					)}
				</Box>
				<Stack spacing="3xs" pt="md">
					<Text size="sm" fw="bold" lineClamp={1}>
						{reportName}
					</Text>
					<Group position="left">
						<Text size="xs" color="text/secondary/default">
							Last edited {getDaysAgo(report.updated_at)} &middot;{' '}
							{report.views ?? 0} views
						</Text>
					</Group>
				</Stack>
			</Card>
		</>
	);
}
